import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, AlertCircle, Receipt } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const POLL_MS = 30000;

function fmtTime(v) {
  if (!v) return "";
  return new Date(v).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function billText(b) {
  if (b.days_left < 0) return `${-b.days_left} day${b.days_left === -1 ? "" : "s"} overdue`;
  return b.days_left === 0 ? "Due today" : "Due tomorrow";
}

export default function NotificationBell() {
  const { activeBranchId } = useBranch();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [bills, setBills] = useState([]); // own service bills due within 1 day; stays until marked Paid
  const [open, setOpen] = useState(false);
  const seenIds = useRef(null); // null = not yet initialized for this branch

  const loadBills = useCallback(async () => {
    if (!isOwner) return;
    try {
      const res = await api.get("/billing-services");
      setBills((res.data || []).filter((b) => b.alert));
    } catch (e) {
      /* ignore */
    }
  }, [isOwner]);

  useEffect(() => {
    setBills([]);
    if (!isOwner) return;
    loadBills();
    const id = setInterval(loadBills, POLL_MS);
    window.addEventListener("billing-updated", loadBills);
    return () => {
      clearInterval(id);
      window.removeEventListener("billing-updated", loadBills);
    };
  }, [isOwner, loadBills]);

  const load = useCallback(async (isFirstLoad) => {
    if (!activeBranchId) return;
    try {
      const res = await api.get("/alerts", { params: { branch_id: activeBranchId } });
      const list = res.data || [];
      setAlerts(list);

      if (isFirstLoad) {
        seenIds.current = new Set(list.map((a) => a.id));
        setUnread(0);
        return;
      }
      const fresh = list.filter((a) => !seenIds.current?.has(a.id));
      if (fresh.length) {
        fresh.forEach((a) => {
          toast.error(`${a.contact_name}: ${a.template_name || "message"} failed`, {
            description: a.failure_reason || undefined,
            duration: 3000,
          });
        });
        seenIds.current = new Set(list.map((a) => a.id));
        setUnread((n) => n + fresh.length);
      }
    } catch (e) {
      /* ignore */
    }
  }, [activeBranchId]);

  useEffect(() => {
    seenIds.current = null;
    setUnread(0);
    setAlerts([]);
    if (!activeBranchId) return;
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(id);
  }, [activeBranchId, load]);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) setUnread(0);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative h-9 w-9 rounded-md flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all duration-150"
        >
          <Bell className="h-5 w-5" />
          {unread + bills.length > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="absolute top-1 right-1 h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center"
            >
              {unread + bills.length > 9 ? "9+" : unread + bills.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {bills.length > 0 && (
          <div className="border-b border-white/10" data-testid="bill-alerts">
            <div className="px-4 py-3">
              <p className="text-sm font-bold text-red-500">Bills due</p>
              <p className="text-xs text-slate-400">Click Paid on the Bill Tracking page to clear</p>
            </div>
            <div className="divide-y divide-white/10">
              {bills.map((b) => (
                <button
                  key={b.id}
                  onClick={() => { setOpen(false); navigate("/bill-tracking"); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-start gap-2"
                >
                  <Receipt className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white truncate">{b.name}</p>
                    <p className="text-xs text-red-400">{billText(b)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="px-4 py-3 border-b border-white/10">
          <p className="text-sm font-bold text-white">Recent Alerts</p>
          <p className="text-xs text-slate-400">Failed messages for this branch</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {alerts.length ? (
            <div className="divide-y divide-white/10">
              {alerts.map((a) => (
                <div key={a.id} data-testid={`alert-${a.id}`} className="px-4 py-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-white truncate">{a.contact_name}</p>
                        <span className="text-[10px] text-slate-400 shrink-0">{fmtTime(a.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{a.template_name || "message"}</p>
                      {a.failure_reason && (
                        <p className="text-xs text-red-400 mt-0.5 truncate">{a.failure_reason}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-slate-400">No recent alerts</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
